// ─────────────────────────────────────────────────────────────────────────
// renderComparisonPdf(model)
//
// Renders a buildComparisonPdfModel() output into a print-ready HTML
// document (landscape A4). Render-only — no calculation, no note-fetching,
// no knowledge of Mode/Scope beyond printing the labels already resolved
// onto model.header. Deterministic: same model in, same HTML out.
// ─────────────────────────────────────────────────────────────────────────

const BRAND = {
  navy: '#0F2744', teal: '#0D7A6E', blue: '#1D6FA4', red: '#C0392B',
  amber: '#B7770D', green: '#1A7A4A',
  gray1: '#F4F7FA', gray2: '#E8EDF3', border: '#D0DAE6',
  text1: '#0F1E2D', text2: '#2C3E50', text3: '#5D7080', text4: '#8FA3B1',
}

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]))
}
function fmt(n) {
  if (n == null || n === 0) return '—'
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n)
}
function fmtSigned(n) {
  const v = n || 0
  if (v === 0) return '—'
  return (v >= 0 ? '+' : '') + fmt(v)
}
function statusColor(status) {
  return status === 'new' ? BRAND.blue : status === 'removed' ? BRAND.red : status === 'changed' ? BRAND.amber : BRAND.text4
}
function statusLabel(status) {
  return status === 'new' ? 'New' : status === 'removed' ? 'Removed' : status === 'changed' ? 'Changed' : 'Unchanged'
}

function renderHeader(header) {
  const logo = header.logoUrl
    ? `<img src="${esc(header.logoUrl)}" alt="logo" style="width:30px;height:30px;object-fit:contain;border-radius:6px" />`
    : `<div style="width:30px;height:30px;border-radius:6px;background:${BRAND.teal}"></div>`

  const comparingRow = (label, rev) => rev ? `
    <div class="cmp-row">
      <span class="cmp-label">${esc(label)}</span>
      <span class="cmp-value">${esc(rev.baseRevisionLabel)} &rarr; ${esc(rev.compareRevisionLabel)}</span>
      ${rev.sameRevision ? `<span class="cmp-flag">Same revision — no change expected</span>` : ''}
    </div>` : ''

  return `
  <div class="rpt-header">
    <div class="rpt-header-row">
      <div class="rpt-brand">${logo}<div><div class="rpt-brand-name">CostSpace Pro</div><div class="rpt-brand-sub">COST MANAGEMENT PLATFORM</div></div></div>
      <div class="rpt-title-block">
        <div class="rpt-project">${esc(header.projectName)}</div>
        <div class="rpt-title">${esc(header.reportTitle)}</div>
      </div>
      <div class="rpt-meta">
        Generated ${esc(header.generatedDate)}${header.generatedBy ? '<br>By ' + esc(header.generatedBy) : ''}
      </div>
    </div>
    <div class="cmp-box">
      <div class="cmp-title">Comparing</div>
      ${header.revisionMeta ? comparingRow('Direct', header.revisionMeta.direct) + comparingRow('Indirect', header.revisionMeta.indirect) : ''}
      <div class="cmp-row" style="margin-top:4px">
        <span class="cmp-label">Mode</span>
        <span class="cmp-value">${esc(header.modeLabel)}</span>
        <span class="cmp-flag" style="background:none;color:${BRAND.text3};font-weight:500">Scope: ${esc(header.scopeLabel)}</span>
      </div>
    </div>
  </div>`
}

function renderSummary(summary) {
  const cards = [
    ['Total Variance', fmtSigned(summary.totalVariance), summary.totalVariance >= 0 ? BRAND.green : BRAND.red],
    ['Variance %', (summary.totalVariancePct || 0).toFixed(1) + '%', summary.totalVariance >= 0 ? BRAND.green : BRAND.red],
    ['Changed Items', summary.changedCount, BRAND.amber],
    ['New Items', summary.newCount, BRAND.blue],
    ['Removed Items', summary.removedCount, BRAND.red],
  ]
  const levelLabel = (lvl) => lvl === 'costcode' ? 'Cost Code' : lvl === 'resource' ? 'Resource' : 'Sub Code'
  const levelBadgeColor = (lvl) => lvl === 'costcode' ? BRAND.navy : lvl === 'resource' ? '#7C3AED' : BRAND.text3
  const levelBadgeBg = (lvl) => lvl === 'costcode' ? BRAND.gray2 : lvl === 'resource' ? '#F3EEFC' : BRAND.gray1

  const top = summary.topVariances.length > 0 ? `
    <div class="top-var-box">
      <div class="top-var-title">Top 10 Variances</div>
      ${summary.topVariances.map(n => `
        <div class="top-var-row">
          <span class="top-var-badge" style="color:${levelBadgeColor(n.level)};background:${levelBadgeBg(n.level)}">${levelLabel(n.level)}</span>
          <span class="top-var-code">${esc(n.code)}</span>
          <span class="top-var-desc">${esc(n.description)}</span>
          <span style="color:${n.variance >= 0 ? BRAND.green : BRAND.red};font-weight:700">${fmtSigned(n.variance)}</span>
        </div>`).join('')}
    </div>` : ''

  return `
  <div class="summary-cards">
    ${cards.map(([label, value, color]) => `
      <div class="summary-card">
        <div class="sc-label">${label}</div>
        <div class="sc-value" style="color:${color}">${value}</div>
      </div>`).join('')}
  </div>
  ${top}`
}

function levelRowBg(level) {
  return level === 0 ? BRAND.gray1 : level === 1 ? '#F3EEFC' : '#fff'
}
function levelCodeColor(level) {
  return level === 0 ? BRAND.navy : level === 1 ? '#7C3AED' : BRAND.text2
}
function levelCodeWeight(level) {
  return level === 0 ? 800 : level === 1 ? 700 : 500
}

function renderRow(r) {
  const indentPx = 10 + r.indent * 20
  const borderColor = r.status !== 'unchanged' ? statusColor(r.status) : 'transparent'
  return `
  <tr style="background:${levelRowBg(r.indent)}">
    <td style="padding-left:${indentPx}px;border-left:3px solid ${borderColor}" class="td-code">
      <span style="color:${levelCodeColor(r.indent)};font-weight:${levelCodeWeight(r.indent)}">${esc(r.code)}</span>
    </td>
    <td class="td-desc">${esc(r.description)}</td>
    <td class="td-num">${fmt(r.baseValue)}</td>
    <td class="td-num">${fmt(r.compareValue)}</td>
    <td class="td-num" style="color:${r.variance >= 0 ? BRAND.green : BRAND.red};font-weight:700">${fmtSigned(r.variance)}</td>
    <td class="td-note">${esc(r.note)}</td>
  </tr>`
}

function renderSection(section) {
  return `
  <div class="section-block">
    <div class="section-title">${esc(section.title)}</div>
    <table>
      <thead>
        <tr><th>Code · Description</th><th></th><th>Version 1</th><th>Version 2</th><th>Variance</th><th>Note</th></tr>
      </thead>
      <tbody>
        ${section.rows.map(renderRow).join('')}
      </tbody>
    </table>
  </div>`
}

const STYLES = `
  @page { size: A4 landscape; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: ${BRAND.text1}; margin: 0; font-size: 10px; }
  .rpt-header { border-bottom: 2px solid ${BRAND.teal}; padding-bottom: 10px; margin-bottom: 14px; }
  .rpt-header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
  .rpt-brand { display: flex; align-items: center; gap: 8px; }
  .rpt-brand-name { font-size: 12px; font-weight: 800; }
  .rpt-brand-sub { font-size: 7.5px; color: ${BRAND.text4}; letter-spacing: .05em; }
  .rpt-title-block { text-align: center; }
  .rpt-project { font-size: 13px; font-weight: 700; }
  .rpt-title { font-size: 11px; color: ${BRAND.text3}; }
  .rpt-meta { font-size: 9px; color: ${BRAND.text3}; text-align: right; }
  .cmp-box { background: ${BRAND.gray1}; border: 1px solid ${BRAND.border}; border-radius: 6px; padding: 8px 10px; }
  .cmp-title { font-size: 8px; font-weight: 800; color: ${BRAND.text3}; text-transform: uppercase; letter-spacing: .06em; margin-bottom: 5px; }
  .cmp-row { display: flex; align-items: center; gap: 8px; margin-bottom: 3px; }
  .cmp-label { font-size: 8.5px; font-weight: 700; color: #065F46; text-transform: uppercase; letter-spacing: .04em; min-width: 50px; }
  .cmp-value { font-size: 10.5px; font-weight: 600; }
  .cmp-flag { font-size: 8.5px; font-weight: 700; color: ${BRAND.amber}; background: #FEF3C7; padding: 2px 7px; border-radius: 999px; }
  .summary-cards { display: flex; gap: 10px; margin-bottom: 10px; }
  .summary-card { flex: 1; border: 1px solid ${BRAND.border}; border-radius: 6px; padding: 8px 10px; }
  .sc-label { font-size: 8px; color: ${BRAND.text3}; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 3px; }
  .sc-value { font-size: 14px; font-weight: 800; }
  .top-var-box { border: 1px solid ${BRAND.border}; border-radius: 6px; padding: 8px 10px; margin-bottom: 16px; }
  .top-var-title { font-size: 10px; font-weight: 700; margin-bottom: 6px; }
  .top-var-row { display: flex; align-items: center; gap: 8px; font-size: 9.5px; padding: 4px 0; border-bottom: 1px dotted ${BRAND.border}; }
  .top-var-badge { font-size: 7.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; padding: 2px 6px; border-radius: 3px; min-width: 52px; text-align: center; flex-shrink: 0; }
  .top-var-code { font-family: monospace; font-weight: 600; color: ${BRAND.text2}; flex-shrink: 0; }
  .top-var-desc { color: ${BRAND.text3}; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .section-block { margin-bottom: 18px; page-break-inside: avoid; }
  .section-title { font-size: 11.5px; font-weight: 800; color: ${BRAND.navy}; margin-bottom: 6px; padding-bottom: 4px; border-bottom: 2px solid ${BRAND.gray2}; }
  table { width: 100%; border-collapse: collapse; font-size: 9px; }
  thead tr { background: ${BRAND.navy}; }
  thead th { color: white; text-transform: uppercase; letter-spacing: .04em; font-size: 7.5px; font-weight: 700; padding: 5px 8px; text-align: right; }
  thead th:nth-child(1), thead th:nth-child(2), thead th:nth-child(6) { text-align: left; }
  tbody td { padding: 4px 8px; border-bottom: 1px solid ${BRAND.gray2}; }
  .td-code { font-family: monospace; font-weight: 600; white-space: nowrap; }
  .td-desc { color: ${BRAND.text2}; }
  .td-num { text-align: right; font-variant-numeric: tabular-nums; }
  .td-note { color: ${BRAND.text2}; font-style: italic; }
  .pdf-footer { position: fixed; bottom: 0; left: 0; right: 0; font-size: 8px; color: ${BRAND.text4}; display: flex; justify-content: space-between; padding: 4px 0; border-top: 1px solid ${BRAND.border}; }
`

export function renderComparisonPdf(model) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${esc(model.header.reportTitle)} — ${esc(model.header.projectName)}</title>
<style>${STYLES}</style>
</head>
<body>
  ${renderHeader(model.header)}
  ${renderSummary(model.summary)}
  ${model.sections.map(renderSection).join('')}
  <div class="pdf-footer">
    <span>${esc(model.header.reportTitle)} · ${esc(model.header.projectName)}</span>
    <span>🔒 Strictly Confidential</span>
  </div>
</body>
</html>`
}
